<template>
  <ApolloQuery
    :query="contributorsQuery"
    :variables="{
      storyId: storyIri + $route.params.id,
    }"
    :update="(data) => data.story.storyContributions"
    @result="setContributors"
    v-slot="{ result: { loading, error } }"
  >
      <v-row v-if="error">
        {{ error }}
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-sheet color="secondaryBackground" class="rounded-xl pa-5">
            <v-container class="contributor-list-container" fluid>
              <v-row align="center">
                <v-col cols="auto">
                  <h1>
                    {{ $t("storyHub.publishing.wizard.contributorCredits") }}
                  </h1>
                </v-col>
                <v-col cols="auto" offset="1">
                  <v-chip class="px-15 contributor-count">{{
                    nonSponsorContributions.length
                  }}</v-chip>
                </v-col>
              </v-row>
              <div v-if="!loading">
                <v-row
                  class="contributor-row my-n4" 
                  v-for="contribution in nonSponsorContributions"
                  :key="contribution.id"
                >
                  <v-col>
                    <ContributorListing
                      :contribution="contribution"
                      @delete="contributorDelete(contributor)"
                      :editable="editable"
                    />
                  </v-col>
                </v-row>
              </div>
              <div v-else>
              <v-skeleton-loader
                v-for="item in [1,2]"
                :key=item
                v-bind="attrs"
                class="my-3"
                type="list-item-avatar-three-line"
              ></v-skeleton-loader>
              </div>
              <v-row v-if="editable">
                <v-col>
                  <v-sheet class="rounded-lg px-2 py-0">
                    <v-container fluid
                      ><v-row align="center">
                        <v-col md="3">
                          <ChooseUser
                            v-model="contributorToAdd"
                            :role="creatorRole"
                            :label="
                              $t(
                                'storyHub.publishing.wizard.addUsernameOrWebsite'
                              )
                            "
                            :canAdd="true"
                          />
                        </v-col>
                        <v-col offset="1" md="3">
                          <ChooseRole v-model="contributorToAddRole" />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-sheet>
                </v-col>
              </v-row>
              <v-row v-if="editable">
                <AddContribution :user="contributorToAdd" :roleId="contributorToAddRole" @added="contributorAdded"/> 
              </v-row>
            </v-container>
          </v-sheet>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-sheet color="secondaryBackground" class="rounded-xl pa-5">
            <v-container fluid class="contributor-list-container">
              <v-row align="center">
                <v-col cols="auto">
                  <h1>
                    {{ $t("storyHub.publishing.wizard.sponsors") }}
                  </h1>
                </v-col>
                <v-col cols="auto" offset="1">
                  <v-chip class="px-15 contributor-count">{{
                    sponsorContributions.length
                  }}</v-chip>
                </v-col>
              </v-row>
              <div v-if="!loading">
              <v-row
                v-for="contribution in sponsorContributions"
                :key="contribution.id"
              >
                <v-col>
                  <ContributorListing
                    :contribution="contribution"
                    :showRole="false"
                    :editable="editable"
                    @delete="contributorDelete(contributor)"
                  />
                </v-col>
              </v-row>
              </div>
              <div v-else>
              <v-skeleton-loader
                v-for="item in [1,2]"
                :key=item
                v-bind="attrs"
                class="my-3"
                type="list-item-avatar-three-line"
              ></v-skeleton-loader>
              </div>
              <v-row v-if="editable">
                <v-col>
                  <v-sheet class="rounded-lg px-8 py-0">
                    <v-container fluid>
                      <v-row align="center" no-gutters>
                        <v-col md="3">
                          <ChooseUser
                            v-model="sponsorToAdd"
                            :role="sponsorRole"
                            :label="
                              $t('storyHub.publishing.wizard.addUsername')
                            "
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-sheet>
                </v-col>
              </v-row>
              <v-row v-if="editable">
                <AddContribution :user="sponsorToAdd" roleId="/story_contribution_roles/Sponsor" @added="sponsorAdded"/> 
              </v-row>
            </v-container>
          </v-sheet>
        </v-col>
      </v-row>
  </ApolloQuery>
</template>
<script>
import ChooseUser from "./ChooseUser";
import ChooseRole from "./ChooseRole";
import ContributorListing from "./ContributorListing";
import AddContribution from "./AddContribution";
import StoryMutations from "@/graphql/StoryMutations";
import { USER_ROLES } from "@/const"
import iris from "@/iris";
export default {
  components: {
    AddContribution,
    ChooseRole,
    ChooseUser,
    ContributorListing,
  },
  props: {
    editable: {
      type: Boolean, 
      default: true,
    }
  },
  data() {
    return {
      contributorToAdd: null,
      contributorToAddRole: "",
      sponsorToAdd: null,
      sponsorContributions: [],
      nonSponsorContributions: [],
    };
  },
  methods: {
    sponsorAdded() {
      this.sponsorToAdd = null;
    },
    contributorAdded() {
      this.contributorToAdd = null;
      this.contributorToAddRole = "";
    },
    contributorDelete(contributor) {
      this.contributors.splice(this.contributors.indexOf(contributor), 1);
    },
    setContributors({ data }) {
      if (data) {
        let sponsorContributions = [];
        let nonSponsorContributions = [];
        for (const contribution of data) {
          if (contribution.role._id == "Sponsor") {
            sponsorContributions.push(contribution);
          } else {
            nonSponsorContributions.push(contribution);
          }
        }
        this.sponsorContributions = sponsorContributions;
        this.nonSponsorContributions = nonSponsorContributions;
      }
    },
  },
  computed: {
    contributorsQuery() {
      return StoryMutations.getStoryContributions;
    },
    storyIri() {
      return iris.story;
    },
    creatorRole() {
      return USER_ROLES.Creator
    },
    sponsorRole() {
      return USER_ROLES.Sponsor
    },
  },
};
</script>
<style scoped>
.v-chip.v-size--default.contributor-count {
  font-size: 1.4em;
}
.contributor-list-container .row .v-sheet {
  background-color: #d3eff8;
}

.contributor-list-container .row:nth-child(2n) .v-sheet {
  background-color: #a9e1f3;
}
</style>
